/**
 * @fileoverview Cross-browser helper that triggers a "save as" download from
 * either an in-memory Blob or a remote URL (e.g., S3 presigned URL).
 *
 * Hidden behind the `projectDownload` feature flag at the call sites.
 */

/**
 * Triggers a browser download for the given source.
 *
 * - When `source` is a Blob, an object URL is created and revoked after the
 *   click event fires (next tick) so the browser has time to read the bytes.
 * - When `source` is a string URL (e.g., a presigned S3 URL), the URL is used
 *   directly via the anchor element's `href` + `download` attributes. Note
 *   that some servers may ignore the `download` attribute when the
 *   `Content-Disposition` header is missing, in which case the browser will
 *   still navigate to the URL — acceptable for our usage since S3 returns a
 *   downloadable response.
 *
 * @param source - Either an in-memory Blob or a string URL.
 * @param filename - The suggested filename. Browsers may sanitize this.
 */
export function triggerBrowserDownload(
  source: Blob | string,
  filename: string,
): void {
  const objectUrl =
    typeof source === "string" ? source : URL.createObjectURL(source);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  // `rel=noopener` mitigates a tab-napping vector when href is cross-origin.
  anchor.rel = "noopener";
  // Hidden — we don't actually want this anchor in the DOM. Required in
  // Firefox where dispatching click on a detached element is a no-op.
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  if (typeof source !== "string") {
    // Defer revoke to ensure the browser has begun the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}
